import React, { useState } from "react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import OperationsPageHeader from "../home/top_bar_components/OperationsPageHeader";
import SubTabs from "../home/top_bar_components/SubTabs";
import LogbookTodayCanvas from "./today/LogbookTodayCanvas";
import LogbookMultiStoreDaily from "./today/LogbookMultiStoreDaily";
import LogbookLayoutBuilder from "./configure/LogbookLayoutBuilder";
import EmailPreviewModal from "./configure/EmailPreviewModal";
import LogbookReportingDisplay from "./LogbookReportingDisplay";
import LogbookAskDisplay from "./LogbookAskDisplay";

// The Logbook hub, on the Tasks-hub pattern (DailyPlanDisplay): one header,
// underline tabs, and the contextual controls right-aligned in the tab row.
//   Daily     - the block canvas (categories, metrics, tables, agents)
//   Reports   - Logbook Analytics, unchanged audience (admin/internal only)
//   Configure - the layout builder + category management (admin/internal only;
//               replaces the old Logbook Categories sidebar page)
// Employees with the logbook_access override see Daily only.
const ADMIN_ROLES = ["rightwork_internal", "company_admin"];

const addDays = (date, delta) => {
  const next = new Date(date);
  next.setDate(next.getDate() + delta);
  return next;
};

const formatHeaderDate = (d) =>
  d.toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

// Temporarily hidden; flip to true to bring the "Preview email" buttons back.
const SHOW_EMAIL_PREVIEW = false;

function NavBtn({ onClick, label, children }) {
  return (
    <button
      onClick={onClick}
      aria-label={label}
      className="w-8 h-8 shrink-0 inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 hover:border-slate-400 shadow-sm"
    >
      {children}
    </button>
  );
}

export default function LogbookHubDisplay(props) {
  const isAdminRole = ADMIN_ROLES.includes(props.permissionRole);
  const canManage = [
    "rightwork_internal",
    "company_admin",
    "company_manager",
  ].includes(props.permissionRole);
  // Reports (and Chat with logbook behind it) are manager-level; the backend report
  // endpoints are store-employee gated and Ask is store-manager gated.
  const canViewAnalytics = canManage;
  // initialView carries the legacy tab values (logbookReporting ->
  // "analytics", logbookConfiguration -> "configure"); honor them only for
  // roles that could see the old tabs.
  const [activeTab, setActiveTab] = useState(() => {
    if (props.initialView === "analytics" && canManage) return "reports";
    if (props.initialView === "configure" && isAdminRole) return "configure";
    return "today";
  });

  // Switching to a store where the user is manager/employee gates the
  // privileged tabs away, but the initializer above never re-runs - without
  // this reset the canvas would render under a stuck, inaccessible
  // configure/reports tab with Daily's controls missing.
  React.useEffect(() => {
    if (!isAdminRole && activeTab === "configure") {
      setActiveTab("today");
    }
    if (!canViewAnalytics && activeTab === "reports") {
      setActiveTab("today");
    }
  }, [isAdminRole, canViewAnalytics, activeTab]);
  // Ask the logbook lives behind a "Chat with logbook" button on the Reports tab
  // (mirroring AnalyticsHubDisplay / KnowledgeHubDisplay). Leaving Reports
  // drops back to the report view; the ask component unmounts, so its
  // conversation resets like the other chat surfaces.
  const [reportsView, setReportsView] = useState("reports"); // "reports" | "ask"
  React.useEffect(() => {
    if (activeTab !== "reports") setReportsView("reports");
  }, [activeTab]);
  const [date, setDate] = useState(new Date());
  const [responsesOutstanding, setResponsesOutstanding] = useState(0);
  const [showEmailPreview, setShowEmailPreview] = useState(false);

  // Daily -> All locations: the logbook-enabled stores in this company where
  // the user is manager+ (the daily email's audience). storeNames is already
  // the user's open-store memberships, ordered company then store name.
  // storeOptions still carries ARCHIVED memberships (status "archived"), and
  // the store permission check does not filter on is_archived, so skip them
  // here or a removed manager would get that location's section.
  const [allLocations, setAllLocations] = useState(false);
  const allLocationStores = React.useMemo(() => {
    const roleByStore = {};
    (props.storeOptions || []).forEach((membership) => {
      if (membership.status === "archived") return;
      roleByStore[membership.store] = membership.permission_type;
    });
    return (props.storeNames || [])
      .filter(
        (store) =>
          store.logbook_enabled &&
          store.company === props.homeCompany &&
          ["rightwork_internal", "company_admin", "company_manager"].includes(
            roleByStore[store.id]
          )
      )
      .map((store) => ({
        id: store.id,
        store_name: store.store_name,
        permission_type: roleByStore[store.id],
      }));
  }, [props.storeNames, props.storeOptions, props.homeCompany]);
  // Eligibility is per store (allLocationStores is already manager+ only),
  // not the home store's role: staff at the home store who manage two other
  // locations still get the view.
  const canViewAllLocations = allLocationStores.length > 1;
  React.useEffect(() => {
    if (!canViewAllLocations && allLocations) {
      setAllLocations(false);
    }
  }, [canViewAllLocations, allLocations]);
  const showAllLocations = allLocations && canViewAllLocations;

  const tabs = [{ key: "today", label: "Daily" }];
  if (canViewAnalytics) {
    tabs.push({ key: "reports", label: "Reports" });
  }
  if (isAdminRole) {
    tabs.push({ key: "configure", label: "Configure" });
  }

  // Ask the logbook takes over the page in the shared chat layout (same
  // as Analytics -> AI Insights and Knowledge Base -> Assistant); the
  // component unmounts on back, so the conversation resets.
  if (activeTab === "reports" && canViewAnalytics && reportsView === "ask") {
    return (
      <LogbookAskDisplay
        homeStore={props.homeStore}
        onBack={() => setReportsView("reports")}
        backLabel="Reports"
      />
    );
  }

  return (
    <div className="my-5">
      <OperationsPageHeader
        title="Logbook"
        scope="store"
        homeStore={props.homeStore}
        storeOptions={props.storeOptions}
        storeNames={props.storeNames}
        change_home_store={props.change_home_store}
      >
        <SubTabs
          className="mt-4"
          fullWidth
          tabs={tabs}
          activeTab={activeTab}
          onChange={setActiveTab}
          rightSlot={
            <>
              {/* Email preview buttons hidden for now (SHOW_EMAIL_PREVIEW). */}
              {SHOW_EMAIL_PREVIEW &&
                activeTab === "configure" &&
                isAdminRole && (
                  <button
                    onClick={() => setShowEmailPreview(true)}
                    className="text-xs font-semibold text-slate-600 bg-white border border-slate-200 rounded-lg px-3 py-1.5 hover:border-slate-400"
                  >
                    ✉ Preview email
                  </button>
                )}
              {/* Right-aligned; the arrows + fixed-width date sit at the
                  far right and the variable badge stays to their left, so
                  the arrows never move: hit Previous, hit Previous again,
                  same spot. */}
              {activeTab === "today" && (
                <>
                  {/* Managers only: employees cannot submit required
                      responses (the backend manager-gates is_response), so
                      the badge would be an actionable warning they can
                      never clear. All locations reports a total over
                      managed stores only, so it shows there regardless of
                      the home store's role. */}
                  {responsesOutstanding > 0 && (canManage || showAllLocations) && (
                    <span className="mr-1 text-xs font-semibold text-rose-700 bg-rose-50 rounded-lg px-3 py-1.5 whitespace-nowrap">
                      {responsesOutstanding}{" "}
                      {responsesOutstanding === 1 ? "response" : "responses"}{" "}
                      required
                    </span>
                  )}
                  {SHOW_EMAIL_PREVIEW && isAdminRole && (
                    <button
                      onClick={() => setShowEmailPreview(true)}
                      className="mr-1 text-xs font-semibold text-slate-600 bg-white border border-slate-200 rounded-lg px-3 py-1.5 hover:border-slate-400"
                    >
                      ✉ Preview email
                    </button>
                  )}
                  <NavBtn
                    onClick={() => setDate(addDays(date, -1))}
                    label="Previous day"
                  >
                    <ChevronLeftIcon style={{ fontSize: "20px" }} />
                  </NavBtn>
                  <span className="w-56 text-center text-sm font-semibold text-slate-800 tabular-nums whitespace-nowrap">
                    {formatHeaderDate(date)}
                  </span>
                  <NavBtn
                    onClick={() => setDate(addDays(date, 1))}
                    label="Next day"
                  >
                    <ChevronRightIcon style={{ fontSize: "20px" }} />
                  </NavBtn>
                </>
              )}
            </>
          }
        />
      </OperationsPageHeader>

      <div className="mt-5 mx-10 pb-20">
        {activeTab === "reports" && canViewAnalytics ? (
          <LogbookReportingDisplay
            // Remount per store: the report's filters (selectedStores,
            // categories, authors) are seeded once from homeStore, so a
            // header store switch would otherwise keep querying the old
            // store under the new store's metadata.
            key={props.homeStore}
            homeStore={props.homeStore}
            homeCompany={props.homeCompany}
            storeNames={props.storeNames}
            onOpenAsk={() => setReportsView("ask")}
          />
        ) : activeTab === "configure" && isAdminRole ? (
          <LogbookLayoutBuilder
            homeStore={props.homeStore}
            homeCompany={props.homeCompany}
            storeNames={props.storeNames}
          />
        ) : (
          <>
            {/* Above the canvas, not in the tab row: that row already holds
                the badge, fixed-width date, and arrows, and a third control
                overflows it at narrow widths. */}
            {canViewAllLocations && (
              <div className="mb-4 inline-flex rounded-lg border border-slate-200 bg-white p-0.5 shadow-sm">
                {[
                  { value: false, label: "This store" },
                  { value: true, label: "All locations" },
                ].map((option) => (
                  <button
                    key={option.label}
                    type="button"
                    onClick={() => setAllLocations(option.value)}
                    aria-pressed={showAllLocations === option.value}
                    className={`text-xs font-semibold rounded-md px-3 py-1 whitespace-nowrap ${
                      showAllLocations === option.value
                        ? "bg-violet-50 text-violet-800"
                        : "text-slate-500 hover:text-slate-800"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
            {showAllLocations ? (
              <LogbookMultiStoreDaily
                date={date}
                stores={allLocationStores}
                homeCompany={props.homeCompany}
                opsRefreshSignal={props.opsRefreshSignal}
                onResponsesOutstandingChange={setResponsesOutstanding}
              />
            ) : (
              <LogbookTodayCanvas
                date={date}
                storeId={props.homeStore}
                homeCompany={props.homeCompany}
                opsRefreshSignal={props.opsRefreshSignal}
                canManage={canManage}
                onResponsesOutstandingChange={setResponsesOutstanding}
              />
            )}
          </>
        )}
      </div>

      <EmailPreviewModal
        open={showEmailPreview}
        onClose={() => setShowEmailPreview(false)}
        homeStore={props.homeStore}
        date={activeTab === "today" ? date : undefined}
      />
    </div>
  );
}
